import { useCallback, useEffect, useState } from "react";
import { EmailSuggestInput } from "../../components/EmailSuggestInput";
import { toast } from "sonner";
import { MailPlus, Loader2, UserCheck, Clock, Trash2, Ban, Power } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API, withCredentials: true });

export default function AdminEmailSetup({ onAuthFail, user }) {
  const isCeo = (user?.email || "").toLowerCase() === "ceo@nexsecure.in" || user?.role === "ceo";
  const [form, setForm] = useState({ name: "", email: "", personal_email: "", temp_password: "" });
  const [staff, setStaff] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/mail-onboard");
      setStaff(data.staff);
    } catch { /* list optional */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/mail-onboard", form);
      if (data.imap_ok) {
        toast.success(`Welcome email sent — ${data.email} is ready for first sign-in`);
      } else {
        toast.warning(`Welcome email sent, but the mail server doesn't recognize ${data.email} yet — create the mailbox in cp.serverbyt.in (Email Accounts → Add Email Account) with exactly this temporary password, or match its existing password.`, { duration: 12000 });
      }
      setForm({ name: "", email: "", personal_email: "", temp_password: "" });
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Could not create the account entry");
      }
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-600 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
  const label = "block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2";

  return (
    <div className="space-y-8" data-testid="email-setup">
      <div className="border border-white/15 bg-white/[0.02] p-5 sm:p-6">
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2 mb-2"><MailPlus size={13} className="text-nx-goldlight" /> New staff email account</p>
        <p className="text-xs text-slate-500 mb-5 leading-relaxed">
          Create the mailbox in your hosting panel (serverbyt) with a temporary password first, then register it here — the welcome email goes to the staff member&apos;s personal address, and webmail forces them to set their own password on first sign-in.
        </p>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Staff member&apos;s name</label>
            <input required className={input} placeholder="e.g. Anjali Nair" value={form.name} onChange={set("name")} data-testid="email-setup-name" />
          </div>
          <div>
            <label className={label}>New official email ID</label>
            <EmailSuggestInput required type="email" className={input} placeholder="name@nexsecure.in" value={form.email} onChange={set("email")} data-testid="email-setup-email" />
          </div>
          <div>
            <label className={label}>Staff member&apos;s personal email</label>
            <EmailSuggestInput required type="email" className={input} placeholder="personal@gmail.com" value={form.personal_email} onChange={set("personal_email")} data-testid="email-setup-personal" />
          </div>
          <div>
            <label className={label}>Temporary password (must match the mailbox password set in the serverbyt panel)</label>
            <input required minLength={6} className={input} placeholder="same as the panel password" value={form.temp_password} onChange={set("temp_password")} data-testid="email-setup-temp-password" autoComplete="off" />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-colors disabled:opacity-60" data-testid="email-setup-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <MailPlus size={14} />} Send welcome email
            </button>
          </div>
        </form>
      </div>

      <div className="border border-white/15 bg-white/[0.02] p-5 sm:p-6" data-testid="email-setup-list">
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-4">Onboarded staff</p>
        {staff === null ? (
          <p className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</p>
        ) : staff.length === 0 ? (
          <p className="text-xs text-slate-500">No staff onboarded yet — the first welcome email you send appears here.</p>
        ) : (
          <div className="flex flex-col divide-y divide-white/10">
            {staff.map((s) => (
              <div key={s.email} className="flex flex-wrap items-center gap-3 py-3" data-testid={`email-setup-row-${s.email}`}>
                <div className="w-full sm:w-auto sm:flex-1 min-w-0 break-words">
                  <p className="text-sm text-white break-words">{s.name || s.email}</p>
                  <p className="text-xs text-slate-500 break-all">{s.email}{s.personal_email ? ` · welcome sent to ${s.personal_email}` : ""}</p>
                </div>
                {s.login_disabled ? (
                  <span className="flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border border-red-500/60 bg-red-500/10 text-red-300 px-2.5 py-1" data-testid={`email-setup-status-${s.email}`}><Ban size={11} /> Disabled</span>
                ) : s.must_change_password ? (
                  <span className="flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border border-amber-500/50 text-amber-300 px-2.5 py-1" data-testid={`email-setup-status-${s.email}`}><Clock size={11} /> Awaiting first sign-in</span>
                ) : (
                  <span className="flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase border border-emerald-500/50 text-emerald-300 px-2.5 py-1" data-testid={`email-setup-status-${s.email}`}><UserCheck size={11} /> Active — own password set</span>
                )}
                {s.must_change_password && (
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/api/admin/mail-onboard/verify", { email: s.email });
                        if (data.imap_ok) toast.success(`Mail server accepts the password for ${s.email} — ready to sign in`);
                        else toast.error(`The mail server doesn't recognize ${s.email} yet. In cp.serverbyt.in → Email Accounts: if it's not in the list, use "Add Email Account" to create it with the exact temporary password you entered here; if it is listed, Modify it and set that same password. Then Re-check.`, { duration: 15000 });
                      } catch { toast.error("Could not check right now"); }
                    }}
                    className="text-[10px] tracking-[0.1em] uppercase border border-white/15 text-slate-400 hover:border-nx-gold/60 hover:text-nx-goldlight px-3 py-1.5 transition-colors"
                    data-testid={`email-setup-recheck-${s.email}`}
                  >
                    Re-check
                  </button>
                )}
                <button
                  onClick={async () => {
                    const pw = window.prompt(`Re-issue a temporary password for ${s.email}.\n\nSet this same password on the mailbox in cp.serverbyt.in first, then enter it here (min 5 characters):`);
                    if (pw === null) return;
                    if (pw.trim().length < 5) { toast.error("Temporary password must be at least 5 characters"); return; }
                    try {
                      const { data } = await api.post("/api/admin/mail-onboard/resend", { email: s.email, temp_password: pw.trim() });
                      if (data.imap_ok) toast.success(`New temporary password emailed to ${s.email} — they'll set their own on next sign-in`);
                      else toast.warning(`Password emailed, but the mail server doesn't accept it yet for ${s.email}. Set the same password in cp.serverbyt.in (Email Accounts → Modify), then use Re-check.`, { duration: 14000 });
                      load();
                    } catch (err) {
                      if (err?.response?.status === 401) onAuthFail();
                      else { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not re-issue the password"); }
                    }
                  }}
                  className="text-[10px] tracking-[0.1em] uppercase border border-white/15 text-slate-400 hover:border-nx-gold/60 hover:text-nx-goldlight px-3 py-1.5 transition-colors"
                  data-testid={`email-setup-resend-${s.email}`}
                >
                  Resend temp password
                </button>
                {isCeo && (
                  <button
                    onClick={async () => {
                      const willDisable = !s.login_disabled;
                      const ok = window.confirm(
                        willDisable
                          ? `Disable webmail login for "${s.email}"?\n\nThey will be signed out immediately and will see "Your email login has been disabled. Please contact your administrator." on any sign-in attempt.`
                          : `Re-enable webmail login for "${s.email}"?\n\nThey will be able to sign in again.`
                      );
                      if (!ok) return;
                      try {
                        await api.post("/api/admin/mail-onboard/toggle-login", { email: s.email, disabled: willDisable });
                        toast.success(willDisable ? `Webmail login disabled for ${s.email}` : `Webmail login re-enabled for ${s.email}`);
                        load();
                      } catch (err) {
                        if (err?.response?.status === 401) onAuthFail();
                        else { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not update login status"); }
                      }
                    }}
                    className={`flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase border px-3 py-1.5 transition-colors ${s.login_disabled ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/70" : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:border-amber-500/70"}`}
                    data-testid={`email-setup-toggle-login-${s.email}`}
                  >
                    {s.login_disabled ? <><Power size={12} /> Enable login</> : <><Ban size={12} /> Disable login</>}
                  </button>
                )}
                {isCeo && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete the staff email account "${s.email}" from this list?\n\nThis removes their onboarding record and webmail session. This cannot be undone.`)) return;
                      try {
                        await api.delete(`/api/admin/mail-onboard/${encodeURIComponent(s.email)}`);
                        toast.success(`Removed ${s.email} from the onboarded staff list`);
                        load();
                      } catch (err) {
                        if (err?.response?.status === 401) onAuthFail();
                        else { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not delete this account"); }
                      }
                    }}
                    className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase border border-red-500/40 text-red-300 hover:bg-red-500/10 hover:border-red-500/70 px-3 py-1.5 transition-colors"
                    data-testid={`email-setup-delete-${s.email}`}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
